import { useState, useEffect } from 'react';
import { Link2, X, Save, Loader2, Check, AlertTriangle, ArrowLeft, Search } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import api from '../../utils/api';

// Konstanten
const UNITS = ['Meter', 'Stueck', 'Rolle', 'kg', 'Liter', 'Paket', 'Set'];
const CATEGORIES = [
  'Kabel',
  'Leitungen',
  'Schaltgeräte',
  'Schutzschaltgeräte',
  'Schalter',
  'Steckdosen',
  'Verteiler',
  'Einbaugeräte',
  'Reihenklemmen',
  'Beleuchtung',
  'Messtechnik',
  'Dimmer',
  'Rohre',
  'Werkzeug',
  'Befestigung',
  'Brandschutz',
  'Kommunikationstechnik',
  'Sonstiges',
];

const SUPPLIER_INFO = {
  obeta: { name: 'Obeta', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  sonepar: { name: 'Sonepar', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
  unielektro: { name: 'UniElektro', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' },
  zajadacz: { name: 'Zajadacz', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
};

/**
 * Product URL Modal - Paste URLs from supplier websites
 */
const ProductUrlModal = ({ isOpen, onClose, onProductSaved }) => {
  // Step state
  const [step, setStep] = useState('urls'); // 'urls' | 'result'

  // URL input state
  const [urlInput, setUrlInput] = useState('');

  // Fetching state
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Loading progress state
  const [loadingStep, setLoadingStep] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Result state
  const [productData, setProductData] = useState({
    name: '',
    category: '',
    unit: '',
    search_keywords: '',
  });
  const [supplierResults, setSupplierResults] = useState([]);

  // Reset state on close
  const handleClose = () => {
    setUrlInput('');
    setProductData({
      name: '',
      category: '',
      unit: '',
      search_keywords: '',
    });
    setSupplierResults([]);
    setStep('urls');
    setError(null);
    onClose();
  };

  // Parse URLs from textarea
  const parseUrls = () => {
    return urlInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0 && (line.startsWith('http://') || line.startsWith('https://')));
  };

  // Fetch product data from URLs
  const fetchUrls = async () => {
    const urls = parseUrls();

    if (urls.length === 0) {
      setError('Bitte mindestens eine gueltige URL eingeben');
      return;
    }

    if (urls.length > 6) {
      setError('Maximal 6 URLs erlaubt');
      return;
    }

    setIsFetching(true);
    setError(null);
    setLoadingProgress(0);
    setLoadingStep('Browser werden gestartet...');

    // Progress messages to show while waiting
    const progressMessages = [
      'Browser werden gestartet...',
      'Bei Lieferanten einloggen...',
      'Obeta wird abgefragt...',
      'Sonepar wird abgefragt...',
      'UniElektro wird abgefragt...',
      'Zajadacz wird abgefragt...',
      'Preise werden extrahiert...',
      'Daten werden verarbeitet...',
    ];

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      stepIndex++;
      setLoadingStep(progressMessages[Math.min(stepIndex, progressMessages.length - 1)]);
      setLoadingProgress(Math.min(90, stepIndex * 10));
    }, 8000); // Update every 8 seconds

    try {
      // Use longer timeout (2.5 minutes) for scraping multiple suppliers
      const response = await api.post('/products/scrape-urls', { urls }, {
        timeout: 150000, // 2.5 minutes
      });

      clearInterval(progressInterval);
      setLoadingProgress(100);

      if (response.data.success) {
        const result = response.data;

        // Set product data
        setProductData({
          name: result.product?.name || '',
          category: result.product?.suggestedCategory || '',
          unit: result.product?.suggestedUnit || '',
          search_keywords: result.product?.suggestedKeywords || '',
        });

        // Set supplier results
        setSupplierResults(result.suppliers || []);

        setStep('result');
      } else {
        setError(response.data.error || 'Fehler beim Abrufen der Produktdaten');
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Fetch URLs error:', err);

      // Better error messages
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Zeitüberschreitung - die Abfrage dauert zu lange. Bitte erneut versuchen.');
      } else {
        setError(err.response?.data?.error || err.message || 'Fehler beim Abrufen der URLs');
      }
    } finally {
      setIsFetching(false);
      setLoadingStep('');
      setLoadingProgress(0);
    }
  };

  // Update supplier field
  const updateSupplierField = (index, field, value) => {
    setSupplierResults(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Recalculate pricePerUnit if price or packageSize changed
      if (field === 'price' || field === 'packageSize') {
        const price = parseFloat(field === 'price' ? value : updated[index].price);
        const pkgSize = parseFloat(field === 'packageSize' ? value : updated[index].packageSize);
        if (price && pkgSize && pkgSize > 0) {
          updated[index].pricePerUnit = Math.round((price / pkgSize) * 100) / 100;
        }
      }

      return updated;
    });
  };

  // Save product
  const saveProduct = async () => {
    if (!productData.name?.trim()) {
      setError('Produktname erforderlich');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Find best price
      const prices = supplierResults
        .filter(s => s.success && s.price)
        .map(s => parseFloat(s.price));
      const bestPrice = prices.length > 0 ? Math.min(...prices) : null;

      // Find supplier with best price
      const bestSupplier = supplierResults.find(s =>
        s.success && parseFloat(s.price) === bestPrice
      );

      // Build supplier_links
      const supplierLinks = supplierResults
        .filter(s => s.success)
        .map(s => ({
          name: SUPPLIER_INFO[s.code]?.name || s.code,
          url: s.url,
        }));

      // Create product
      const createData = {
        name: productData.name,
        category: productData.category,
        unit: productData.unit,
        price: bestPrice,
        supplier: SUPPLIER_INFO[bestSupplier?.code]?.name || null,
        supplier_link: bestSupplier?.url || null,
        supplier_links: supplierLinks,
        search_keywords: productData.search_keywords
          .split(',')
          .map(k => k.trim())
          .filter(Boolean),
      };

      console.log('💾 [ProductUrlModal] Creating product:', createData);

      const productResponse = await api.post('/products', createData);
      const newProductId = productResponse.data?.id;
      console.log('💾 [ProductUrlModal] Product created, ID:', newProductId);

      if (newProductId) {
        // Add supplier sources
        console.log('💾 [ProductUrlModal] Adding sources for', supplierResults.length, 'suppliers...');
        for (const supplier of supplierResults) {
          const shouldSave = supplier.success || supplier.sku || supplier.price;
          console.log(`💾 [ProductUrlModal] Supplier ${supplier.code}: shouldSave=${shouldSave}, success=${supplier.success}, sku=${supplier.sku}, price=${supplier.price}`);

          if (shouldSave) {
            try {
              const sourceData = {
                supplier: SUPPLIER_INFO[supplier.code]?.name || supplier.code,
                price: supplier.price ? parseFloat(supplier.price) : null,
                article_number: supplier.sku || null,
                package_size: supplier.packageSize ? parseInt(supplier.packageSize, 10) : null,
                price_per_unit: supplier.pricePerUnit || null,
                url: supplier.url || null,
              };
              console.log(`💾 [ProductUrlModal] POST /products/${newProductId}/sources:`, sourceData);
              await api.post(`/products/${newProductId}/sources`, sourceData);
              console.log(`✅ [ProductUrlModal] Source ${supplier.code} saved!`);
            } catch (err) {
              console.error(`❌ [ProductUrlModal] Source for ${supplier.code} failed:`, err.response?.data || err.message);
            }
          }
        }
      }

      onProductSaved?.(productResponse.data);
      handleClose();
    } catch (err) {
      console.error('Save product error:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  // Get supported shops text
  const supportedShops = 'Obeta, Sonepar, UniElektro, Zajadacz';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-primary-500" />
          <span>Produkt per URL anlegen</span>
          {step === 'result' && (
            <span className="text-sm font-normal text-gray-500">- Ergebnis</span>
          )}
        </div>
      }
      size="xl"
    >
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1: Enter URLs */}
        {step === 'urls' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Produkt-URLs einfuegen (eine pro Zeile)
              </label>
              <textarea
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={`https://www.obeta.de/article/0110512
https://www.sonepar.de/product/0230026
https://shop.unielektro.de/artikel/11102140
https://www.zajadacz.de/artikel/110511`}
                className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
              <p className="mt-2 text-sm text-gray-500">
                Unterstuetzte Shops: <span className="font-medium">{supportedShops}</span>
              </p>
            </div>

            {/* URL Preview */}
            {parseUrls().length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Erkannte URLs ({parseUrls().length})
                </h4>
                <div className="space-y-2">
                  {parseUrls().map((url, index) => {
                    let supplier = null;
                    try {
                      const hostname = new URL(url).hostname.toLowerCase();
                      if (hostname.includes('obeta')) supplier = 'obeta';
                      else if (hostname.includes('sonepar')) supplier = 'sonepar';
                      else if (hostname.includes('unielektro')) supplier = 'unielektro';
                      else if (hostname.includes('zajadacz')) supplier = 'zajadacz';
                    } catch (e) {
                      // Ignore
                    }

                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-2 text-sm ${supplier ? SUPPLIER_INFO[supplier]?.bgLight : 'bg-gray-100'} px-3 py-1.5 rounded`}
                      >
                        {supplier ? (
                          <span className={`font-medium ${SUPPLIER_INFO[supplier]?.textColor}`}>
                            {SUPPLIER_INFO[supplier]?.name}
                          </span>
                        ) : (
                          <span className="text-gray-500">Unbekannt</span>
                        )}
                        <span className="text-gray-400">|</span>
                        <span className="text-gray-600 truncate flex-1">{url}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Loading Progress */}
            {isFetching && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-6 h-6 text-orange-500 animate-spin flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-700">
                      {loadingStep || 'Preise werden abgerufen...'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Dies kann bis zu 2 Minuten dauern (4 Shops werden abgefragt)
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${loadingProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={handleClose} disabled={isFetching}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                onClick={fetchUrls}
                disabled={parseUrls().length === 0 || isFetching}
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Abrufen...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Abrufen ({parseUrls().length} URLs)
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Step 2: Results */}
        {step === 'result' && (
          <>
            {/* Success Count */}
            <div className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              <span className="font-medium">
                {supplierResults.filter(s => s.success).length} von {supplierResults.length} URLs erfolgreich abgerufen
              </span>
            </div>

            {/* Product Info */}
            <div className="space-y-4">
              <Input
                label="Produktname *"
                value={productData.name}
                onChange={(e) => setProductData({ ...productData, name: e.target.value })}
                placeholder="z.B. NYY-J 5x16 RE Erdkabel"
              />

              <div className="grid grid-cols-3 gap-4">
                <Select
                  label="Kategorie"
                  value={productData.category}
                  onChange={(e) => setProductData({ ...productData, category: e.target.value })}
                  options={[
                    { value: '', label: 'Waehlen...' },
                    ...CATEGORIES.map(c => ({ value: c, label: c })),
                  ]}
                />
                <Select
                  label="Einheit"
                  value={productData.unit}
                  onChange={(e) => setProductData({ ...productData, unit: e.target.value })}
                  options={[
                    { value: '', label: 'Waehlen...' },
                    ...UNITS.map(u => ({ value: u, label: u })),
                  ]}
                />
                <Input
                  label="Suchbegriffe"
                  value={productData.search_keywords}
                  onChange={(e) => setProductData({ ...productData, search_keywords: e.target.value })}
                  placeholder="NYY-J, 5x16, ..."
                />
              </div>
            </div>

            {/* Supplier Results */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Abgerufene Preise</h3>
              <div className="border rounded-lg overflow-hidden">
                {supplierResults.map((supplier, index) => {
                  const info = SUPPLIER_INFO[supplier.code] || { name: 'Unbekannt', color: 'bg-gray-500', textColor: 'text-gray-700' };
                  const isSuccess = supplier.success;

                  return (
                    <div
                      key={index}
                      className={`
                        flex items-center gap-4 p-3
                        ${index > 0 ? 'border-t' : ''}
                        ${isSuccess ? 'bg-green-50' : 'bg-red-50'}
                      `}
                    >
                      {/* Status Icon */}
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                        ${isSuccess ? 'bg-green-500 text-white' : 'bg-red-300 text-white'}
                      `}>
                        {isSuccess ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </div>

                      {/* Supplier Name */}
                      <div className="w-28 flex-shrink-0">
                        <span className={`font-medium ${info.textColor}`}>{info.name}</span>
                        {!isSuccess && supplier.error && (
                          <span className="block text-xs text-red-600 truncate" title={supplier.error}>
                            {supplier.error}
                          </span>
                        )}
                      </div>

                      {/* Data Fields */}
                      {isSuccess ? (
                        <div className="flex-1">
                          <div className="grid grid-cols-3 gap-2">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-14">Art.Nr</span>
                              <Input
                                value={supplier.sku || ''}
                                onChange={(e) => updateSupplierField(index, 'sku', e.target.value)}
                                className="text-sm flex-1"
                                placeholder="-"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-10">Preis</span>
                              <div className="relative flex-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={supplier.price || ''}
                                  onChange={(e) => updateSupplierField(index, 'price', e.target.value)}
                                  className="text-sm pr-6"
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-6">VE</span>
                              <Input
                                type="number"
                                value={supplier.packageSize || ''}
                                onChange={(e) => updateSupplierField(index, 'packageSize', e.target.value)}
                                className="text-sm w-20"
                                placeholder="-"
                              />
                              {supplier.pricePerUnit && (
                                <span className="text-xs text-green-600 font-medium whitespace-nowrap ml-1">
                                  = {supplier.pricePerUnit.toFixed(2)}€/E
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Copper Surcharge Info */}
                          {supplier.copperSurcharge && (
                            <div className="text-xs text-orange-600 mt-1 pl-14">
                              (inkl. {supplier.copperSurcharge.toFixed(2)}€ Kupfer)
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex-1 text-sm text-gray-500">
                          Keine Daten (Login erforderlich?)
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep('urls');
                  setSupplierResults([]);
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Zurueck
              </Button>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleClose}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  onClick={saveProduct}
                  disabled={!productData.name?.trim() || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Speichern
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProductUrlModal;
